import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { useProducts } from "../context/ProductsContext";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const currency = (n) => `$${n.toFixed(2)}`;

export default function Shop() {
  const { add } = useCart();
  const { products } = useProducts();
  return (
    <div data-testid="shop-page" className="bg-[color:var(--sr-bg)] pt-32 md:pt-40 pb-24 md:pb-40">
      <div className="sr-container">
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-7">
            <p className="label-eyebrow text-[color:var(--sr-primary)] mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> Shop · 5 bottles
            </p>
            <h1 className="font-serif text-6xl md:text-8xl leading-[0.9] tracking-tight text-[color:var(--sr-primary)]">
              The full <br />
              <em className="italic font-light text-[color:var(--sr-accent)]">collection.</em>
            </h1>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 text-[color:var(--sr-text)]/75 leading-relaxed self-end">
            Small batches. Numbered releases. Free ground shipping on any order
            of six bottles or more.
          </div>
        </div>

        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-16 md:gap-y-24">
          {products.map((p, idx) => (
            <motion.li
              key={p.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.9, delay: idx * 0.04 }}
              className={`group ${idx % 3 === 1 ? "lg:mt-24" : ""} ${idx % 3 === 2 ? "lg:mt-12" : ""}`}
              data-testid={`shop-product-${p.id}`}
            >
              <Link to={`/product/${p.id}`} className="block">
                <div className="relative aspect-[4/5] overflow-hidden bg-[color:var(--sr-secondary)]">
                  <span className="absolute z-10 top-4 left-4 label-eyebrow text-[color:var(--sr-primary)]/60">
                    N°{p.number}
                  </span>
                  <div
                    className="absolute inset-0 opacity-70 mix-blend-multiply"
                    style={{
                      background: `radial-gradient(60% 55% at 50% 55%, ${p.accent}22 0%, transparent 65%)`,
                    }}
                  />
                  <img
                    src={p.image}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      add({ id: p.id, lookup_key: p.lookup_key, name: p.name, price: p.price, image: p.image }, 1);
                      toast.success(`${p.name} added to bag`);
                    }}
                    data-testid={`shop-quick-add-${p.id}`}
                    className="absolute bottom-4 right-4 flex items-center gap-2 px-4 py-2.5 bg-[color:var(--sr-bg)] text-[color:var(--sr-primary)] label-eyebrow opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500"
                    aria-label={`Add ${p.name}`}
                  >
                    Add <Plus size={14} strokeWidth={1.5} />
                  </button>
                </div>
                <div className="mt-5 flex items-baseline justify-between gap-4">
                  <p className="font-serif text-2xl text-[color:var(--sr-primary)] group-hover:text-[color:var(--sr-accent)] transition-colors">
                    {p.name}
                  </p>
                  <p className="font-serif text-xl text-[color:var(--sr-primary)] tabular-nums">
                    {currency(p.price)}
                  </p>
                </div>
              </Link>
            </motion.li>
          ))}
        </ul>
      </div>
    </div>
  );
}
